/**
 * applyPrimitive — the single imperative entry point that runs a primitive on
 * an element via the Web Animations API, with:
 *  - automatic JuiceService tier handling (call sites NEVER branch on tier)
 *  - prefers-reduced-motion collapse to the 200ms crossfade
 *  - CSS-class fallback (juice.css) when element.animate is unavailable.
 */

import type { JuiceService } from "../service.js";
import { juice } from "../service.js";
import type { PrimitiveAnimation, PrimitiveOpts } from "./keyframes.js";
import {
  breathe,
  cascadeStep,
  gutter,
  kick,
  magnetRecede,
  pop,
  pulseRing,
  reducedCrossfade,
  rise,
  shakeRotate,
  shakeX,
  shimmer,
  stamp,
} from "./keyframes.js";

export type PrimitiveName =
  | "pop"
  | "stamp"
  | "rise"
  | "breathe"
  | "pulse-ring"
  | "shimmer"
  | "shake-1"
  | "shake-2"
  | "shake-3"
  | "shake-x"
  | "gutter"
  | "magnet"
  | "kick";

function build(name: PrimitiveName, opts: PrimitiveOpts): PrimitiveAnimation {
  switch (name) {
    case "pop":
      return pop(opts);
    case "stamp":
      return stamp(opts);
    case "rise":
      return rise(opts);
    case "breathe":
      return breathe(opts);
    case "pulse-ring":
      return pulseRing(opts);
    case "shimmer":
      return shimmer(opts);
    case "shake-1":
      return shakeRotate(1, opts);
    case "shake-2":
      return shakeRotate(2, opts);
    case "shake-3":
      return shakeRotate(3, opts);
    case "shake-x":
      return shakeX(opts);
    case "gutter":
      return gutter(opts);
    case "magnet":
      return magnetRecede(opts);
    case "kick":
      return kick(opts);
  }
}

/** Idle-loop primitives that should not run at all under reduced motion
 *  (a crossfade replacement makes no sense for an infinite loop). */
const IDLE_LOOPS: ReadonlySet<PrimitiveName> = new Set(["breathe", "pulse-ring", "shimmer"]);

export interface ApplyOptions extends PrimitiveOpts {
  service?: JuiceService;
}

/**
 * Runs the named primitive on `el`. Returns the Animation, or null when the
 * primitive was suppressed by the ladder (e.g. SHIMMER in TRIM/LITE, idle
 * loops under reduced motion) or ran via the CSS fallback.
 */
export function applyPrimitive(
  el: HTMLElement,
  name: PrimitiveName,
  opts: ApplyOptions = {},
): Animation | null {
  const service = opts.service ?? juice;

  // Ladder: SHIMMER is the first decorative cut (off outside FULL).
  if (name === "shimmer" && !service.shimmerEnabled()) return null;

  const reduced = service.reducedMotion();
  if (reduced && IDLE_LOOPS.has(name)) return null;

  const anim: PrimitiveAnimation = reduced
    ? reducedCrossfade({ delayMs: opts.delayMs ?? 0 })
    : build(name, opts);

  if (typeof el.animate === "function") {
    return el.animate(anim.keyframes, anim.options);
  }

  // CSS fallback — same values, defined in juice.css.
  el.classList.add("jz", anim.cssClass);
  const isLoop = anim.options.iterations === Infinity;
  if (!isLoop) {
    const onEnd = () => {
      el.classList.remove(anim.cssClass);
      el.removeEventListener("animationend", onEnd);
    };
    el.addEventListener("animationend", onEnd);
  }
  return null;
}

/** CASCADE — RISE across `container`'s children, 60ms stagger, DOM order. */
export function applyCascade(container: HTMLElement, opts: ApplyOptions = {}): Animation[] {
  const service = opts.service ?? juice;
  const out: Animation[] = [];
  const children = Array.from(container.children).filter(
    (c): c is HTMLElement => c instanceof HTMLElement,
  );
  children.forEach((child, i) => {
    if (service.reducedMotion()) {
      const a = applyPrimitive(child, "rise", { ...opts, delayMs: 0, service });
      if (a) out.push(a);
      return;
    }
    const step = cascadeStep(i, opts);
    if (typeof child.animate === "function") {
      out.push(child.animate(step.keyframes, step.options));
    } else {
      child.classList.add("jz", step.cssClass);
      child.style.animationDelay = `${step.options.delay ?? 0}ms`;
    }
  });
  return out;
}
